import React, { useLayoutEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import { Box, Divider, Grid, Link, useMediaQuery, useTheme as useMuiTheme } from "@mui/material"
import { InputText, Snackbar, Typography } from "@synapse-analytics/synapse-ui"
import { useFormikContext } from "formik"

// Add this import
import { UseCaseCard } from "../../../components/cards/UseCaseCard"
import { getTheme } from "../../../hooks/UseTheme"
import { getHostnames } from "../../../utils/genericHelpers"
import type { ProjectCreationFormikValues, ProjectCreationOverviewProps } from "../interfaces"

/**
 * Project creation overview, first screen in the creation flow
 * @param  {string} errorMessage error message to display
 * @return  {React.ReactElement}
 */
export function ProjectCreationOverview(props: Readonly<ProjectCreationOverviewProps>): React.ReactElement {
  const { errorMessage } = props
  const { t } = useTranslation("projects")

  const { values, errors, touched, setFieldValue, handleChange, handleBlur } =
    useFormikContext<ProjectCreationFormikValues>()

  const theme = getTheme()
  const MuiTheme = useMuiTheme()

  const isLgDown = useMediaQuery(MuiTheme.breakpoints.down("lg"))

  const firstTextRef = useRef<HTMLElement>(null)
  const secondTextRef = useRef<HTMLElement>(null)

  const [height, setHeight] = useState<number>()

  // layout-specific effect to calculate the height of the larger text to avoid height inconsistency
  useLayoutEffect(() => {
    function handleResize(): void {
      if (secondTextRef.current && firstTextRef.current) {
        // Reset the height of both components to 'auto' to allow for natural height calculation
        secondTextRef.current.style.height = "auto"
        firstTextRef.current.style.height = "auto"

        // Get the natural height of both components
        const firstHeight = secondTextRef.current.offsetHeight
        const secondHeight = firstTextRef.current.offsetHeight

        const maxHeight = Math.max(firstHeight, secondHeight)
        secondTextRef.current.style.height = `${maxHeight}px`
        firstTextRef.current.style.height = `${maxHeight}px`
        setHeight(maxHeight)
      }
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    window.addEventListener("zoom", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
      window.removeEventListener("zoom", handleResize)
    }
  }, [])

  return (
    <Grid container item direction="column" spacing={2}>
      {errorMessage && (
        <Grid item>
          <Snackbar severity="error" fullWidth description={errorMessage} />
        </Grid>
      )}

      <Grid item container spacing={1}>
        <Grid item xs={12} md={6}>
          <InputText
            required
            fullWidth
            id="name"
            label={t("creationFlow.fields.name")}
            placeholder={t("creationFlow.fields.namePlaceholder")}
            value={values.name}
            handleChange={handleChange}
            handleBlur={handleBlur}
            error={touched.name && Boolean(errors.name) && errors.name}
            description={t("creationFlow.validation.required")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <InputText
            fullWidth
            label={t("creationFlow.fields.description")}
            id="description"
            placeholder={t("creationFlow.fields.descriptionPlaceholder")}
            value={values.description}
            handleChange={handleChange}
            handleBlur={handleBlur}
            error={touched.description && Boolean(errors.description) && errors.description}
            description={t("creationFlow.fields.descriptionOptional")}
          />
        </Grid>
      </Grid>

      {/* Use-case section */}
      <Grid container item xs={12}>
        <Grid item xs md={6} lg={4}>
          <Typography variant="h3-bold" gutterBottom style={{ paddingRight: "4px" }}>
            {t("creationFlow.sections.autoML.title")}
          </Typography>

          <Typography
            variant="label-regular"
            gutterBottom
            style={{ paddingRight: "12px", position: "relative", height }}
            ref={firstTextRef}
          >
            {t("creationFlow.sections.autoML.description")}
          </Typography>
          <Box display="flex" flexWrap="wrap" justifyContent="space-between">
            <UseCaseCard
              name={t("creationFlow.useCases.creditScoring.name")}
              description={t("creationFlow.useCases.creditScoring.description")}
              onClick={() => {
                setFieldValue("type", "credit_scoring_auto-ml")
              }}
              selected={values.type === "credit_scoring_auto-ml" || values.type === "credit_scoring_pre-built"}
            />
          </Box>
        </Grid>
        <Divider
          orientation="vertical"
          flexItem
          light
          sx={{
            "&::before, &::after": {
              borderColor: theme.palette.gray.background[1],
            },
            margin: "15px 5px",
          }}
        >
          <Typography variant="label-regular" disabled>
            {t("creationFlow.sections.divider")}
          </Typography>
        </Divider>

        {/* Deploy your own model section */}
        <Grid item xs>
          <Typography variant="h3-bold" gutterBottom>
            {t("creationFlow.sections.deployOwn.title")}
          </Typography>

          <Typography variant="label-regular" gutterBottom ref={secondTextRef} style={{ position: "relative", height }}>
            {t("creationFlow.sections.deployOwn.description")}{" "}
            <Link
              href={`https://${getHostnames().docs}/`}
              target="_blank"
              style={{ color: theme.palette.base.text[1], textDecoration: "underline" }}
            >
              {t("creationFlow.sections.deployOwn.documentationLink")}
            </Link>{" "}
            {t("creationFlow.sections.deployOwn.descriptionSuffix")}
          </Typography>

          <Grid item container xs={12}>
            <Box display="flex" flexWrap="wrap" justifyContent="space-between">
              <Box marginBottom={isLgDown ? 2 : 0} flexBasis={{ sm: "100%", lg: "calc(50% - 8px)" }}>
                <UseCaseCard
                  name={t("creationFlow.useCases.generic.name")}
                  description={t("creationFlow.useCases.generic.description")}
                  onClick={() => {
                    setFieldValue("type", "generic")
                  }}
                  selected={values.type === "generic"}
                />
              </Box>
              <Box flexBasis={{ sm: "100%", lg: "calc(50% - 8px)" }}>
                <UseCaseCard
                  name={t("creationFlow.useCases.classification.name")}
                  description={t("creationFlow.useCases.classification.description")}
                  onClick={() => {
                    setFieldValue("type", "classification")
                  }}
                  selected={values.type === "classification"}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}
